import { Link } from "react-router-dom";
import { Star, ArrowUpRight } from "lucide-react";
import { GOOGLE_REVIEWS_URL, REVIEWS } from "@/data";
import { Reveal, SectionHead } from "@/components/Reveal";

const STAR_SLOTS = ["one", "two", "three", "four", "five"];

export const Stars = ({ size = "h-4 w-4", count = 5 }) => (
  <div className="flex gap-0.5" aria-label={`${count} out of 5 stars`}>
    {STAR_SLOTS.map((slot, i) => (
      <Star key={slot} className={`${size} ${i < count ? "fill-leaf text-leaf" : "fill-stone/40 text-stone/40"}`} />
    ))}
  </div>
);

export const GoogleMark = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5c-.3 1.5-1.1 2.7-2.4 3.6v3h3.9c2.2-2.1 3.5-5.1 3.5-8.7z" />
    <path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.7-4.9H1.4v3.1C3.4 21.4 7.4 24 12 24z" />
    <path fill="#FBBC05" d="M5.3 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.6.4-2.4V6.5H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.5l3.9-3.1z" />
    <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.6 1.4 6.5l3.9 3.1c1-2.8 3.6-4.8 6.7-4.8z" />
  </svg>
);

export const ReviewCard = ({ r }) => (
  <figure
    data-testid="testimonial-card"
    className="flex h-full flex-col rounded-2xl border border-ink/8 bg-white p-8 shadow-[0_2px_20px_-8px_rgba(18,26,21,0.1)]"
  >
    <div className="flex items-center justify-between">
      <Stars count={r.rating} />
      <GoogleMark />
    </div>
    <blockquote className="mt-5 flex-1 font-serif text-lg italic leading-relaxed text-ink/85">“{r.text}”</blockquote>
    <figcaption className="mt-6 border-t border-ink/10 pt-5">
      <p className="font-sans text-sm font-medium text-ink">
        {r.name} <span className="text-fern">· {r.suburb}</span>
      </p>
      <p className="mt-1 font-mono text-xs tracking-[0.25em] uppercase text-leafdeep">{r.treatment}</p>
    </figcaption>
  </figure>
);

export const GoogleLink = ({ testid, className = "" }) => (
  <a
    href={GOOGLE_REVIEWS_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group inline-flex items-center gap-2 font-sans text-sm font-medium text-ink transition-colors hover:text-leafdeep ${className}`}
  >
    <GoogleMark /> Read all our reviews on Google
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const Testimonials = () => (
  <section id="reviews" data-testid="testimonials-section" className="bg-cream py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
      <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
        <SectionHead
          dark
          overline="Kind words"
          title={
            <>
              What Canberra locals{" "}
              <span className="italic text-fern">say about us.</span>
            </>
          }
        />
        <Reveal delay={0.15}>
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-rating-badge"
            className="flex items-center gap-4 rounded-2xl border border-ink/10 bg-white px-6 py-4 shadow-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-md"
          >
            <span className="font-serif text-4xl text-ink">5.0</span>
            <div>
              <Stars />
              <p className="mt-1 flex items-center gap-1.5 font-sans text-xs text-fern">
                <GoogleMark className="h-3.5 w-3.5" /> Google reviews · Canberra City
              </p>
            </div>
          </a>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
        {REVIEWS.slice(0, 3).map((r, i) => (
          <Reveal key={r.name} delay={0.1 * i} y={24}>
            <ReviewCard r={r} />
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2} className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-10">
        <Link
          to="/testimonials"
          data-testid="testimonials-view-all-link"
          className="group inline-flex items-center gap-2 font-sans text-sm font-medium text-ink transition-colors hover:text-leafdeep"
        >
          Read more from our patients
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
        <GoogleLink testid="testimonials-google-link" />
      </Reveal>
    </div>
  </section>
);

export default Testimonials;
